import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { Logo, PdfExportSettings, PdfOrientation, PdfPaperSize, SynthesisGroup } from '../api/types';
import { LogoPreview } from './LogoPreview';
import { COMPILED_DEFAULT_SETTINGS, compiledScaleToFitOnePage, computeCompiledPagePlan } from '../utils/compiledPdfExport';
import { buildCompiledRows, computeCompiledRowLayout } from '../utils/compiledChartLayout';
import { planDateRange } from '../utils/planDateRange';

/**
 * Réglages d'export PDF du diagramme COMPILÉ — même structure visuelle que
 * PdfExportSettingsModal (logos, lignes, plage de dates, mise en page),
 * mais SANS colonnes ni style de connecteur à choisir (le compilé a des
 * colonnes fixes et jamais de connecteurs, voir CompiledSynthesisChart) et
 * SANS persistance serveur : il n'y a pas de Diagram unique à qui rattacher
 * ces réglages (plusieurs diagrammes source fusionnés), ils vivent le temps
 * de la session (voir `onConfirm`, appelé avec les réglages choisis plutôt
 * qu'un PATCH réseau — voir compiledPdfExport.ts).
 */
const MAX_LOGOS = 3;
const PAPER_SIZES: PdfPaperSize[] = ['A0', 'A1', 'A2', 'A3', 'A4', 'Letter', 'Legal'];

function toDateInput(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

export function CompiledPdfExportSettingsModal({
  groups,
  onClose,
  onConfirm,
}: {
  groups: SynthesisGroup[];
  onClose: () => void;
  onConfirm: (settings: Required<PdfExportSettings>) => void;
}) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<Required<PdfExportSettings>>(COMPILED_DEFAULT_SETTINGS);
  const [logos, setLogos] = useState<Logo[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<Logo[]>('/logos')
      .then(setLogos)
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  }, []);

  const toggleLogo = (logoId: string) => {
    setSettings((prev) => {
      if (prev.logoIds.includes(logoId)) return { ...prev, logoIds: prev.logoIds.filter((id) => id !== logoId) };
      if (prev.logoIds.length >= MAX_LOGOS) return prev;
      return { ...prev, logoIds: [...prev.logoIds, logoId] };
    });
  };

  const relevantGroups = useMemo(
    () => (settings.onlyVisibleRows ? groups.map((g) => ({ ...g, tasks: g.tasks.filter((task) => task.visible) })) : groups),
    [groups, settings.onlyVisibleRows],
  );
  const relevantTasks = useMemo(() => relevantGroups.flatMap((g) => g.tasks), [relevantGroups]);
  const rowLayout = useMemo(() => computeCompiledRowLayout(buildCompiledRows(relevantGroups)), [relevantGroups]);

  const defaultRange = useMemo(() => planDateRange(relevantTasks), [relevantTasks]);

  const { dayCount } = useMemo(() => {
    const start = settings.dateRangeStart ? settings.dateRangeStart.slice(0, 10) : defaultRange?.start;
    const end = settings.dateRangeEnd ? settings.dateRangeEnd.slice(0, 10) : defaultRange?.end;
    if (!start || !end) return { dayCount: 30 };
    const days = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86400000) + 1;
    return { dayCount: Math.max(1, days) };
  }, [defaultRange, settings.dateRangeStart, settings.dateRangeEnd]);

  const pagePlan = useMemo(
    () =>
      computeCompiledPagePlan({
        rowLayout,
        dayCount,
        paperSize: settings.paperSize,
        orientation: settings.orientation,
        marginCm: settings.marginCm,
        scalePercent: settings.scalePercent,
      }),
    [rowLayout, dayCount, settings.paperSize, settings.orientation, settings.marginCm, settings.scalePercent],
  );

  const handleFitToOnePage = () => {
    const scale = compiledScaleToFitOnePage(rowLayout, dayCount, settings.paperSize, settings.orientation, settings.marginCm);
    setSettings((prev) => ({ ...prev, scalePercent: scale, fitToOnePage: true }));
  };

  const handleConfirm = () => {
    onConfirm(settings);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal card" style={{ width: 640, maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('pdfExport.title')}</h3>
          <button className="btn" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>

        {error && <p className="error-text">{error}</p>}

        <h4>{t('pdfExport.logos', { count: settings.logoIds.length, max: MAX_LOGOS })}</h4>
        {logos.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>{t('pdfExport.noLogos')}</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {logos.map((logo) => {
              const checked = settings.logoIds.includes(logo.id);
              const disabled = !checked && settings.logoIds.length >= MAX_LOGOS;
              return (
                <label
                  key={logo.id}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 84, opacity: disabled ? 0.5 : 1 }}
                >
                  <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleLogo(logo.id)} />
                  <LogoPreview svgContent={logo.svgContent} cropX={logo.cropX} cropY={logo.cropY} zoom={logo.zoom} size={56} />
                  <span style={{ fontSize: 11, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                    {logo.companyName}
                  </span>
                </label>
              );
            })}
          </div>
        )}

        <h4>{t('pdfExport.rows')}</h4>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="checkbox"
            checked={settings.onlyVisibleRows}
            onChange={(e) => setSettings((prev) => ({ ...prev, onlyVisibleRows: e.target.checked }))}
          />
          {t('pdfExport.onlyVisibleRows')}
        </label>

        <h4>{t('pdfExport.dateRange')}</h4>
        <div className="toolbar" style={{ marginTop: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {t('synthesis.windowFrom')}
            <input
              type="date"
              value={toDateInput(settings.dateRangeStart) || defaultRange?.start || ''}
              onChange={(e) => setSettings((prev) => ({ ...prev, dateRangeStart: e.target.value ? `${e.target.value}T00:00:00.000Z` : null }))}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {t('synthesis.windowTo')}
            <input
              type="date"
              value={toDateInput(settings.dateRangeEnd) || defaultRange?.end || ''}
              onChange={(e) => setSettings((prev) => ({ ...prev, dateRangeEnd: e.target.value ? `${e.target.value}T00:00:00.000Z` : null }))}
            />
          </label>
          {(settings.dateRangeStart || settings.dateRangeEnd) && (
            <button
              type="button"
              className="btn"
              onClick={() => setSettings((prev) => ({ ...prev, dateRangeStart: null, dateRangeEnd: null }))}
            >
              {t('pdfExport.wholePlan')}
            </button>
          )}
        </div>

        <h4>{t('pdfExport.pageSetup')}</h4>
        <div className="toolbar" style={{ marginTop: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {t('pdfExport.paperSize')}
            <select value={settings.paperSize} onChange={(e) => setSettings((prev) => ({ ...prev, paperSize: e.target.value as PdfPaperSize }))}>
              {PAPER_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {t('pdfExport.orientation')}
            <select
              value={settings.orientation}
              onChange={(e) => setSettings((prev) => ({ ...prev, orientation: e.target.value as PdfOrientation }))}
            >
              <option value="portrait">{t('pdfExport.orientationPortrait')}</option>
              <option value="landscape">{t('pdfExport.orientationLandscape')}</option>
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {t('pdfExport.marginCm')}
            <input
              type="number"
              min={0}
              max={10}
              step={0.01}
              style={{ width: 70 }}
              value={settings.marginCm}
              onChange={(e) => setSettings((prev) => ({ ...prev, marginCm: parseFloat(e.target.value || '0') }))}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {t('pdfExport.scalePercent')}
            <input
              type="number"
              min={10}
              max={400}
              style={{ width: 70 }}
              value={settings.scalePercent}
              onChange={(e) => setSettings((prev) => ({ ...prev, scalePercent: parseInt(e.target.value || '100', 10), fitToOnePage: false }))}
            />
            %
          </label>
          <button type="button" className={`btn ${settings.fitToOnePage ? 'btn-primary' : ''}`} onClick={handleFitToOnePage}>
            {t('pdfExport.fitToOnePage')}
          </button>
        </div>
        <p style={{ color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
          {t('pdfExport.pageEstimate', { wide: pagePlan.pagesWide, tall: pagePlan.pagesTall })}
        </p>

        <div className="toolbar" style={{ marginTop: 12 }}>
          <button className="btn btn-primary" onClick={handleConfirm}>
            {t('diagram.exportPdf')}
          </button>
          <button className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
